import { AlertTriangle, ArrowRight, X, CheckCircle2, Circle, Star, Rocket, ShieldCheck } from "lucide-react"
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"

export default function Notice({ profileCompletion, setShowNotice, userDetail }) {
  
  const getMissingSections = () => {
    if (!userDetail) return [];
    const missing = [];
    if (!userDetail.profile_image) missing.push("Profile Photo");
    if (!userDetail.bio || userDetail.bio.length < 50) missing.push("Complete Bio (min 50 chars)");
    if (!userDetail.skills || userDetail.skills.length === 0) missing.push("Skills & Expertise");
    if (!userDetail.projects || userDetail.projects.length === 0) missing.push("Portfolio Projects");
    if (!userDetail.city || !userDetail.country) missing.push("Location Details");
    return missing;
  };

  const missingSections = getMissingSections();

  return (
    <motion.div 
      initial={{ opacity: 0, y: 40, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="relative max-w-2xl w-full bg-rose-600/90 backdrop-blur-3xl rounded-[2rem] sm:rounded-[3rem] border border-white/30 shadow-[0_50px_120px_rgba(225,29,72,0.3)] overflow-hidden max-h-[90vh] flex flex-col"
    >
      {/* Internal Decorative Glows */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -z-10 animate-pulse" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-rose-900/30 rounded-full blur-[80px] -z-10" />
      
      {/* Close Button */}
      <button
        onClick={() => setShowNotice(false)}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 sm:p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all z-20"
      >
        <X size={18} className="sm:hidden" />
        <X size={20} className="hidden sm:block" />
      </button>

      <div className="p-6 sm:p-10 lg:p-14 overflow-y-auto custom-scrollbar">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          
          {/* Left Column: Progress & Header */}
          <div className="flex-1 space-y-4 sm:space-y-6 lg:space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/20 border border-white/20 backdrop-blur-md">
               <ShieldCheck size={12} className="text-white fill-white sm:size-[14px]" />
               <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-white">Verification Center</span>
            </div>

            <div>
               <h3 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] mb-4 sm:mb-6 tracking-tight">
                 Your Profile is <span className="text-rose-200">{profileCompletion}%</span> Ready
               </h3>
               <p className="text-rose-50 text-base sm:text-lg font-medium leading-relaxed opacity-90">
                 Complete your setup to be visible to thousands of clients and get listed in search results.
               </p>
            </div>

            {/* Premium Progress Bar */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-between items-end">
                 <span className="text-[10px] sm:text-xs font-black text-rose-100 uppercase tracking-widest">Requirement: 80%</span>
                 <span className="text-2xl sm:text-3xl font-black text-white">{profileCompletion}%</span>
              </div>
              <div className="h-3 sm:h-4 w-full rounded-full bg-white/10 overflow-hidden p-0.5 sm:p-1 border border-white/10 backdrop-blur-md">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${profileCompletion}%` }}
                  transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
                  className={`h-full rounded-full shadow-[0_0_20px_rgba(255,255,255,0.4)] ${
                    profileCompletion < 80 ? 'bg-gradient-to-r from-amber-400 to-rose-400' : 'bg-gradient-to-r from-teal-400 to-emerald-400'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Right Column: Checklist */}
          {missingSections.length > 0 && (
            <div className="w-full lg:max-w-[240px] space-y-6 sm:space-y-8">
              <div className="bg-white/10 backdrop-blur-xl p-6 sm:p-8 rounded-[1.5rem] sm:rounded-[2.5rem] border border-white/10">
                <h4 className="text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] text-rose-100 mb-4 sm:mb-6">Missing</h4>
                <ul className="space-y-3 sm:space-y-4">
                  {missingSections.map((item, i) => (
                    <motion.li 
                      key={i}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-white font-bold"
                    >
                      <Circle size={8} className="text-white fill-white animate-pulse sm:size-[10px]" />
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </div>
              
              <Link
                to="/artisan/profile"
                onClick={() => setShowNotice(false)}
                className="w-full flex items-center justify-center gap-2 sm:gap-3 px-6 py-3.5 sm:px-8 sm:py-5 bg-white text-rose-700 font-black rounded-2xl sm:rounded-[2rem] hover:bg-rose-50 hover:scale-[1.02] transition-all shadow-xl active:scale-95 text-sm sm:text-base"
              >
                Finish Now
                <ArrowRight size={18} className="sm:size-5" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
